import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { IncomingInvoice, IncomingInvoiceFormData, Unternehmen } from "@kundeninfo/api-client";
import { Chip, Hinweis, Objekt, Tabelle } from "@kundeninfo/ui/me-design";
import type { ObjektProps } from "@kundeninfo/ui/me-design";
import { KONTIERUNG_TEXT } from "../../components/kontierungTexts.js";
import { Vorgangsdateien } from "../../components/Vorgangsdateien.js";
import { useAssignmentNames } from "../../components/KontierungFelder.js";
import type { ListClient } from "../../components/KontierungFelder.js";
import { kennzeichenFolge, kennzeichenTeil, unternehmenKuerzel } from "../../components/objektPanel/kennzeichen.js";
import type { Kennzeichen } from "../../components/objektPanel/kennzeichen.js";
import { objektFuss } from "../../components/objektPanel/ErsteSpalte.js";
import { oeffneOderSpringe, useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import { formatDatum, formatDezimalBetrag } from "../../format.js";
import { ROW_TEXT } from "../customerOverview/texts.js";
import { TicketAnlegen } from "../tickets/TicketAnlegen.js";
import { useKennungsAttribut } from "../../components/objektPanel/kennungsAttribut.js";
import type { LieferantenOrt, TicketWeg } from "../suppliers/LiefervertragObjekt.js";
import { PAYMENT_TEXT, paymentStatus } from "../suppliers/paymentState.js";
import { periodText, useWithdrawPayment } from "../suppliers/shared.js";
import { SUPPLIER_TEXT_WEB as S } from "../suppliers/texts.js";
import { ErfassungsPanel } from "./ErfassungsPanel.js";
import type { IncomingListClient } from "./KundeEingangsrechnungen.js";
import { chargeText } from "./shared.js";
import { CancelQuestion, ZahlungPanel } from "./ZahlungPanel.js";
import { INCOMING_TEXT_WEB as T } from "./texts.js";
import "./IncomingInvoices.css";

/**
 * DIE EINGANGSRECHNUNG ALS `Objekt` (Spec „Umstellung auf Objekt“, P9, K13; Plan Aufgabe 3) — die EINE Zeile dieser Art in
 * Akte, Einkauf und Objektpanel, gebaut wie der Liefervertrag (`LiefervertragObjekt`): reine Teile-Funktion
 * (`eingangsrechnungTeile`), Details als Kinder (`IncomingDetail`, ohne eigene Aktionsleiste), im Fuß der Verweis
 * (Panelknopf = Bearbeiten, nicht an einer stornierten; Sprung nur außerhalb der eigenen Akte) und das „⋯“ mit „Zahlung
 * bestätigen“, „Zahlung zurücknehmen“, „Stornieren“, „Ticket anlegen“. Die Aktionen hält `useIncomingActions` (PF19).
 */

export type EingangsrechnungTeile = Pick<ObjektProps, "name" | "kennung" | "bezug" | "art" | "wert" | "status" | "statusArt"
  | "zeit"> & { kennzeichen: Kennzeichen[]; angaben: string[] };

/** Die Teile nach Anatomie 1.2: Name Rechnungsnummer des Lieferanten, Kennung E-Nr., Bezug Vertrag (außerhalb der Akte mit
 *  Lieferant), Wert Brutto, Status Zahlstatus, Zeit „fällig am“, Kennzeichen Unternehmen (nur bei mehreren in der Sicht) →
 *  Art (Jahresabrechnung, Gutschrift) → „angenommen“, Angaben Eingang und Leistungszeitraum. */
export function eingangsrechnungTeile(inv: IncomingInvoice,
  o: { mitLieferant: boolean; mehrereUnternehmen?: boolean; sitzung: readonly Unternehmen[] }): EingangsrechnungTeile {
  const status = paymentStatus(inv.payment);
  const bezug = [inv.supplierContractTitle, o.mitLieferant ? inv.customerName : null].filter((x) => x !== null).join(" · ");
  return {
    name: inv.supplierInvoiceNumber, kennung: inv.entryLabel, ...(bezug === "" ? {} : { bezug }), art: "eingangsrechnung",
    wert: formatDezimalBetrag(inv.grossAmount, inv.currency), status: status.text, statusArt: status.art,
    zeit: formatDatum(inv.dueDate),
    kennzeichen: kennzeichenFolge({ unternehmen: unternehmenKuerzel(inv.companyId, o.sitzung, o.mehrereUnternehmen), hinweise: [
      ...(inv.kind === "INVOICE" ? [] : [{ text: T.kind[inv.kind] }]),
      ...(inv.payment.state === "ASSUMED_PAID" ? [{ text: PAYMENT_TEXT.assumed }] : []),
    ] }, o.sitzung),
    angaben: [`${T.receivedOn} ${formatDatum(inv.receivedOn)}`,
      ...(inv.servicePeriodStart === null || inv.servicePeriodEnd === null ? []
        : [`${T.servicePeriod} ${periodText(inv.servicePeriodStart, inv.servicePeriodEnd)}`])],
  };
}

export type EingangsrechnungAktion = "edit" | "pay" | "withdraw" | "cancel" | "ticket";

export function EingangsrechnungObjekt(p: {
  rechnung: IncomingInvoice; ort: LieferantenOrt; aufgeklappt: boolean; onAufgeklappt: (offen: boolean) => void;
  client: IncomingListClient; lists: ListClient; companies: readonly Unternehmen[]; mehrereUnternehmen: boolean;
  mitTicket: boolean; onAktion: (a: EingangsrechnungAktion) => void;
  /** Ohne Angabe springt „Ordner“ in die Dateien des Lieferanten — über `sprungZiel` samt Herkunft und durch den Ausgang
   *  des Gastgebers (PF3, F10). */
  onOrdnerOeffnen?: (folderId: string) => void; jumpNote?: string | null;
  /** Im Objektpanel: eine Datei der Rechnung wurde geschrieben (F9) — die Ebene gilt als geändert. */
  onDateienGeaendert?: () => void;
}): ReactElement {
  const inv = p.rechnung;
  const wege = useObjektPanel();
  const merke = useKennungsAttribut("data-incoming-id", inv.id);
  const { kennzeichen, angaben, ...teile } = eingangsrechnungTeile(inv, { mitLieferant: p.ort !== "akte",
    mehrereUnternehmen: p.mehrereUnternehmen, sitzung: p.companies });
  const offen = inv.status !== "CANCELLED";
  const fuss = objektFuss(wege, {
    verweis: { bezug: { art: "eingangsrechnung", id: inv.id, customerId: inv.customerId, companyId: inv.companyId,
      label: inv.supplierInvoiceNumber },
    ...(offen ? { eigenesPanel: () => p.onAktion("edit") } : { ohnePanel: true }),
    ...(p.ort === "einkauf" ? {} : { ohneSprung: true }) },
    menue: { name: T.actionsFor(inv.supplierInvoiceNumber), onWahl: (w) => p.onAktion(w as EingangsrechnungAktion), eintraege: [
      ...(offen ? [{ wert: "pay", text: T.payAction },
        ...(inv.paidAmount === null ? [] : [{ wert: "withdraw", text: S.withdrawAction }]),
        { wert: "cancel", text: T.cancelAction, gefaehrlich: true }] : []),
      ...(p.mitTicket ? [{ wert: "ticket", text: T.createTicket }] : []),
    ] },
  });
  const ordner = p.onOrdnerOeffnen
    ?? ((folderId: string) => wege.springe({ art: "akte", id: inv.customerId, reiter: "dateien", folderId, label: inv.customerName }));
  return (
    <Objekt ref={merke} {...teile} klappbar aufgeklappt={p.aufgeklappt}
      onAufgeklappt={(d) => p.onAufgeklappt(d.aufgeklappt)} kennzeichen={kennzeichenTeil(kennzeichen)} fuss={fuss}
      angaben={angaben.join(" · ")}>
      {p.aufgeklappt && (
        <IncomingDetail invoice={inv} client={p.client} lists={p.lists} companies={p.companies} onOrdnerOeffnen={ordner}
          jumpNote={p.jumpNote ?? null} {...(p.onDateienGeaendert === undefined ? {} : { onFilesChanged: p.onDateienGeaendert })} />
      )}
    </Objekt>
  );
}

type Offen = { kind: Exclude<EingangsrechnungAktion, "withdraw">; invoice: IncomingInvoice };

/**
 * DIE AKTIONEN EINER EINGANGSRECHNUNG (PF19, PF20) — Akte, Einkauf und Objektpanel nutzen denselben Weg: „Bearbeiten“
 * (`ErfassungsPanel`, `verlauf`), „Zahlung bestätigen“ und „Stornieren“ (gestapelte Fragen), „Zahlung zurücknehmen“
 * (`useWithdrawPayment`; ein Fehlschlag steht als `fehler` da), „Ticket anlegen“ (danach das neue Ticket im Panel). Kunde
 * und Unternehmen vom Beleg. Nach jedem Schreibweg — auch einem gescheiterten — `onDone` mit der Kennung.
 */
export function useIncomingActions(a: { client: IncomingListClient; lists: ListClient; companies: readonly Unternehmen[];
  ticket?: TicketWeg; onDone: (invoiceId: string) => void }): {
  start(kind: EingangsrechnungAktion, inv: IncomingInvoice): void; offen: boolean; mitTicket: boolean; fehler: string | null;
  panels: ReactNode;
} {
  const [offen, setOffen] = useState<Offen | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const withdraw = useWithdrawPayment();
  const wege = useObjektPanel();
  const done = (id: string) => (): void => { setOffen(null); a.onDone(id); };
  async function withdrawPayment(inv: IncomingInvoice): Promise<void> {
    setWithdrawing(true);
    const r = await withdraw({ paidAmount: inv.paidAmount!, paidOn: inv.paidOn, currency: inv.currency,
      run: () => a.client.confirmIncomingPayment(inv.customerId, inv.id, { version: inv.version, paidOn: null, paidAmount: null }) });
    setWithdrawing(false);
    if (r === null) return;
    setFehler(r.failure);
    a.onDone(inv.id);
  }
  const o = offen;
  const kuerzel = (inv: IncomingInvoice): string[] => a.companies.filter((u) => u.id === inv.companyId).map((u) => u.shortCode);
  const panels = o === null ? null
    : o.kind === "edit" ? (
      <ErfassungsPanel customerId={o.invoice.customerId} companyId={o.invoice.companyId} client={a.client} lists={a.lists}
        vorhandene={o.invoice} verlauf onGespeichert={() => a.onDone(o.invoice.id)} onSchliessen={() => setOffen(null)} />
    ) : o.kind === "pay" ? (
      <ZahlungPanel customerId={o.invoice.customerId} client={a.client} invoice={o.invoice}
        onGespeichert={done(o.invoice.id)} onStale={done(o.invoice.id)} onSchliessen={() => setOffen(null)} />
    ) : o.kind === "cancel" ? (
      <CancelQuestion customerId={o.invoice.customerId} client={a.client} invoice={o.invoice}
        onGespeichert={done(o.invoice.id)} onStale={done(o.invoice.id)} onSchliessen={() => setOffen(null)} />
    ) : a.ticket === undefined ? null : (
      <TicketAnlegen client={a.ticket.client} companies={a.companies} ich={a.ticket.ich} vorgabeCompanyId={o.invoice.companyId}
        arbeitsunternehmenId={a.ticket.arbeitsunternehmenId}
        kunde={{ id: o.invoice.customerId, name: o.invoice.customerName, kuerzel: kuerzel(o.invoice) }}
        bezug={{ kind: "INCOMING_INVOICE", id: o.invoice.id,
          label: T.ticketReference(o.invoice.entryLabel, o.invoice.supplierInvoiceNumber), companyId: o.invoice.companyId }}
        onAbbrechen={() => setOffen(null)}
        onAngelegt={(id) => { setOffen(null); oeffneOderSpringe(wege, { art: "ticket", id }); }} />
    );
  return {
    start: (kind, inv) => {
      if (offen !== null || withdrawing || (kind === "ticket" && a.ticket === undefined)) return;
      if (kind === "withdraw") void withdrawPayment(inv);
      else setOffen({ kind, invoice: inv });
    },
    offen: offen !== null || withdrawing, mitTicket: a.ticket !== undefined, fehler, panels,
  };
}

type Charge = IncomingInvoiceFormData["charges"][number];

/** Das Aufgeklappte einer Rechnung (Details als Kinder des `Objekt`, P9): Eckdaten, Positionen mit Kontierung, gebundene
 *  Posten, Dateien — ohne eigene Aktionsleiste, die Aktionen stehen im Fuß. */
function IncomingDetail(p: {
  invoice: IncomingInvoice; client: IncomingListClient; lists: ListClient; companies: readonly Unternehmen[];
  onOrdnerOeffnen: (folderId: string) => void; jumpNote: string | null; onFilesChanged?: () => void;
}): ReactElement {
  const inv = p.invoice;
  const customerId = inv.customerId;
  const { names, failed: namesFailed } = useAssignmentNames(p.lists, inv.companyId);
  /** Die Posten des Vertrags (Erfassungshilfe); die gebundenen leiten sich beim Zeichnen aus `inv.chargeIds` ab — eine
   *  gelöste Bindung (Storno, Umbindung) verschwindet so mit dem neuen Stand (Fix-Runde 1 W1). */
  const [contractCharges, setContractCharges] = useState<{ contractId: string; items: Charge[] } | null>(null);
  const [chargesFailed, setChargesFailed] = useState(false);

  const contractId = inv.supplierContractId;
  const needsCharges = contractId !== null && inv.chargeIds.length > 0;
  const bound = inv.chargeIds.join(",");
  useEffect(() => {
    if (!needsCharges || contractId === null) return undefined;
    let alive = true;
    p.client.getIncomingInvoiceFormData(customerId, { companyId: inv.companyId, contractId }).then((d) => {
      if (alive) { setContractCharges({ contractId, items: d.charges }); setChargesFailed(false); }
    }, () => { if (alive) setChargesFailed(true); });
    return () => { alive = false; };
    // Neu lesen nur, wenn sich Vertrag oder Bindung ändern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.client, customerId, inv.companyId, contractId, needsCharges, bound]);
  const charges = contractCharges === null || contractCharges.contractId !== contractId ? []
    : contractCharges.items.filter((c) => inv.chargeIds.includes(c.id));

  /** Ein Name aus den Listen; solange sie laden „…“, nach einem Ladefehler „–“ (mit Hinweis oben, wie am Vertrag). */
  const name = (map: ReadonlyMap<string, string> | undefined, id: string | null): string =>
    (id === null || namesFailed ? T.none : map?.get(id) ?? T.pending);
  const cancelled = inv.status === "CANCELLED";
  const money = (amount: string): string => formatDezimalBetrag(amount, inv.currency);

  return (
    <div className="inc__detail">
      {p.jumpNote !== null && <p className="inc__jump-note" role="status">{p.jumpNote}</p>}
      {namesFailed && <Hinweis art="warnung">{S.namesLoadFailed}</Hinweis>}
      {needsCharges && chargesFailed && <Hinweis art="warnung">{T.chargesLoadFailed}</Hinweis>}
      <p className="inc__facts">
        <span>{T.kind[inv.kind]}</span>
        <span>{T.invoiceDate} {formatDatum(inv.invoiceDate)}</span>
        {inv.supplierContractTitle !== null && <span>{T.contract} {inv.supplierContractTitle}</span>}
        <span>{S.paymentMethod[inv.paymentMethod]}</span>
        {inv.servicePeriodStart !== null && inv.servicePeriodEnd !== null && (
          <span>{T.servicePeriod} {periodText(inv.servicePeriodStart, inv.servicePeriodEnd)}</span>
        )}
        {inv.payableAmount !== inv.grossAmount && <span>{T.payable} {money(inv.payableAmount)}</span>}
        {inv.paidAmount !== null && inv.paidOn !== null && <span>{T.paid(money(inv.paidAmount), formatDatum(inv.paidOn))}</span>}
        {cancelled && <span>{T.cancelled(inv.cancelReason ?? "")}</span>}
      </p>

      <h4 className="inc__detail-title">{T.lines}</h4>
      <Tabelle dicht stapeln>
        <table>
          <thead>
            <tr>
              <th scope="col" data-ausrichtung="rechts">{T.net}</th>
              <th scope="col" data-ausrichtung="rechts">{T.tax}</th>
              <th scope="col">{T.taxCaseLabel}</th>
              <th scope="col">{KONTIERUNG_TEXT.account}</th>
              <th scope="col">{KONTIERUNG_TEXT.costCentre}</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.id}>
                <td data-ausrichtung="rechts">{money(l.netAmount)}</td>
                <td data-ausrichtung="rechts">{money(l.taxAmount)}</td>
                <td>{S.taxCase[l.taxCase]}</td>
                <td>{l.bookingAccountId === null ? <Chip statusArt="warnung">{T.withoutAccount}</Chip>
                  : name(names?.accounts, l.bookingAccountId)}</td>
                <td>{name(names?.costCentres, l.costCentreId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Tabelle>

      {charges.length > 0 && (
        <>
          <h4 className="inc__detail-title">{T.charges}</h4>
          <ul className="inc__charges-bound">{charges.map((c) => <li key={c.id}>{chargeText(c, inv.currency)}</li>)}</ul>
        </>
      )}

      <h4 className="inc__detail-title">{T.files}</h4>
      <Vorgangsdateien client={p.client} kundeId={customerId} bezug={{ art: "incoming-invoices", id: inv.id }}
        vorgangName={ROW_TEXT.firstLink("INCOMING_INVOICE", inv.entryLabel)} firmen={p.companies} lesend={null}
        onZahl={() => undefined} onOrdnerOeffnen={p.onOrdnerOeffnen}
        {...(p.onFilesChanged === undefined ? {} : { onGeaendert: p.onFilesChanged })} />

    </div>
  );
}
